import { createStore, createSubscriber, createHook, createContainer, StoreActionApi } from 'react-sweet-state'
import { genericActions, ResourceState } from '_core/store'
import { IssueStatus, Membrane, Status } from './types'
import { ClearRequestPayload } from '_core/store'
import history from '_core/history'

export interface MembranesState extends ResourceState<Membrane> {
  schema?: any
  filters: any
  duplicate?: Partial<Membrane>
}
export type MembranesApi = StoreActionApi<MembranesState>

const initialState: MembranesState = {
  endpoint: '/membranes',
  all: [],
  currentId: undefined,
  requests: {},
  schema: undefined,
  filters: {
    status: [Status.toValidate, Status.detailsRequired, Status.accepted]
  },
  duplicate: undefined
}

const actions = {
  fetch: (query?: any) => async ({ dispatch }: MembranesApi) => {
    if (!query)
      query = {}
    await dispatch(genericActions.apiFetch({
      ...query,
      fields: ['name', '_id', 'dates', 'omdId', 'status', 'issues', 'report'],
      populate: ['report']
    }))
  },
  get: (_id: string) => async ({ dispatch }: MembranesApi) => {
    await dispatch(genericActions.apiGet(_id, {
      data: {
        populate: ['history.user', 'notes.user', 'report']
      }
    }))
  },
  create: (data: Membrane) => async ({ getState, dispatch }: MembranesApi) => {
    const res = await dispatch(genericActions.apiCreate(data))
    if (res && res.result)
      history.push(`${getState().endpoint}/${res.result._id}`)
  },
  update: (data: Partial<Membrane>) => async ({ dispatch }: MembranesApi) => {
    const res = await dispatch(genericActions.apiUpdate(data))
    if (res && res.result)
      await dispatch(actions.get(res.result._id))
  },
  createOrUpdate: (data: Partial<Membrane>) => async ({ dispatch }: MembranesApi) => {
    await dispatch(genericActions.apiCreateOrUpdate(data))
  },
  delete: (_id: string) => async ({ dispatch }: MembranesApi) => {
    await dispatch(genericActions.apiDelete(_id))
  },
  createNote: (data: { membraneId: string, comment: string }) =>
    async ({ getState, dispatch }: MembranesApi) => {
      const res = await dispatch(genericActions.api({
        url: `${getState().endpoint}/${data.membraneId}/notes`,
        method: 'post',
        data
      }))
      if (res && res.result)
        dispatch(actions.get(res.result._id))
    },
  updateNote: (data: { membraneId: string, noteId: string, comment: string }) =>
    async ({ getState, dispatch }: MembranesApi) => {
      const res = await dispatch(genericActions.api({
        url: `${getState().endpoint}/${data.membraneId}/notes/${data.noteId}`,
        method: 'put',
        data
      }))
      if (res && res.result)
        dispatch(actions.get(res.result._id))
    },
  deleteNote: (data: { membraneId: string, noteId: string }) =>
    async ({ getState, dispatch }: MembranesApi) => {
      const res = await dispatch(genericActions.api({
        url: `${getState().endpoint}/${data.membraneId}/notes/${data.noteId}`,
        method: 'delete'
      }))
      if (res && res.result)
        dispatch(actions.get(res.result._id))
    },
  updateIssue: (data: { membraneId: string, issueId: string, status: IssueStatus }) =>
    async ({ getState, dispatch }: MembranesApi) => {
      const res = await dispatch(genericActions.api({
        url: `${getState().endpoint}/${data.membraneId}/issues/${data.issueId}`,
        method: 'put',
        data
      }))
      if (res && res.result)
        dispatch(actions.get(res.result._id))
    },
  getSchema: () => async ({ dispatch, getState, setState }: MembranesApi) => {
    const res = await dispatch(genericActions.api({
      method: 'get',
      url: `${getState().endpoint}/schema`
    }))
    setState({ schema: res.result })
  },  
  setFilter: (name: string, value: any) => ({ getState, setState }: MembranesApi) => {
    if (!value) {
      const newFilters = { ...getState().filters }
      delete newFilters[name]
      setState({ filters: newFilters })
    } else {
      setState({
        filters: {
          ...getState().filters,
          [name]: value
        }
      })
    }
  },
  setDuplicate: (membrane?: Membrane) => ({ setState }: MembranesApi) => {
    if (!membrane)
      return setState({ duplicate: undefined })
    const keysToDuplicate = ['name', 'type', 'published', 'structure', 'chemistry', 'modification', 'data', 'report']
    const duplicate = keysToDuplicate.reduce((duplicate, key) => {
      duplicate[key] = membrane[key]
      return duplicate
    }, {})
    setState({ duplicate })
  },
  setCurrent: (membrane?: Membrane) => ({ dispatch }: MembranesApi) => {
    dispatch(genericActions.setCurrent(membrane))
  },
  clearRequest: (data: ClearRequestPayload) => ({ dispatch }: MembranesApi) => {
    dispatch(genericActions.clearRequest(data))
  },
  clearRequests: () => ({ dispatch }: MembranesApi) => {
    dispatch(genericActions.clearRequests())
  },
  getRequest: (method?: string, withId?: boolean) =>
    ({ dispatch }: MembranesApi) =>
      dispatch(genericActions.getRequest(method, withId)),
}

export type MembranesActions = typeof actions

const Store = createStore<MembranesState, MembranesActions>({
  name: 'membranes',
  initialState,
  actions
})

export const MembranesSubscriber = createSubscriber(Store)
export const useMembranes = createHook(Store)
export const useCurrentMembrane = createHook(Store, {
  selector: (state: MembranesState) => {
    if (state.currentId)
      return state.all.find(membrane => membrane._id === state.currentId)
    return 
  }
})
export const MembranesContainer = createContainer(Store)

export default Store